import { LocaleLink } from '../../../i18n/locale-link';
import { LandingSection } from '../layout/landing-section';
import type { PlanCard } from '../../../content/landings/types';

interface Props {
  title: string;
  plan: PlanCard;
  planMicro: string;
  mathTitle: string;
  mathRows: { label: string; value: string; highlight: boolean }[];
  mathMicro: string;
  upsell: string;
  onCtaClick: (label: string) => void;
}

/**
 * Единственный тариф `/ip` — без переключателя периода: годовой ИП-тариф не
 * существует (`ip-yearly` в сиде помечен `active: false`, см. бриф задачи).
 * Рядом — карточка «Простая математика»: тот же приём убеждения, что
 * `PricingThree`, но здесь не варианты тарифа, а окупаемость одного.
 * Разметка — `docs/superpowers/specs/2026-08-14-landings-mockups/landing-ip.html`
 * строки 353–383 (`.price-card` + `.anchor-card`).
 */
export function PricingSingle({
  title,
  plan,
  planMicro,
  mathTitle,
  mathRows,
  mathMicro,
  upsell,
  onCtaClick,
}: Props) {
  return (
    <LandingSection id="pricing" top="sm">
      <h2 className="text-[clamp(26px,4.2vw,38px)] font-extrabold leading-[1.12] tracking-[-0.02em]">
        {title}
      </h2>

      <div className="mt-[26px] grid gap-4 md:grid-cols-2 md:items-start">
        <div className="rounded-2xl border-2 border-brand bg-bone-surface p-[26px] shadow-lg">
          <h3 className="mb-1.5 text-[19px] font-extrabold leading-[1.12]">{plan.name}</h3>
          <div className="whitespace-nowrap text-[30px] font-extrabold leading-[1.1] sm:text-4xl">
            {plan.priceMonthly}
          </div>

          <ul className="my-[18px] grid list-none gap-[9px]">
            {plan.features.map((f) => (
              <li
                key={f}
                className="relative pl-[25px] text-[15px] before:absolute before:left-0 before:font-extrabold before:text-status-active-text before:content-['✓']"
              >
                {f}
              </li>
            ))}
          </ul>

          <LocaleLink
            to="/plans"
            onClick={() => onCtaClick('plan-ip')}
            className="block w-full rounded-lg border-[1.5px] border-brand bg-brand px-6 py-3.5 text-center text-base font-bold text-white no-underline transition-colors hover:border-brand-mid hover:bg-brand-mid"
          >
            {plan.cta}
          </LocaleLink>
          <p className="mt-2.5 text-center text-[13.5px] text-ink-soft">{planMicro}</p>
        </div>

        <div>
          <div className="rounded-2xl border border-dashed border-edge bg-bone-surface p-5 shadow-m-sm">
            <p className="mb-2.5 font-extrabold text-ink">{mathTitle}</p>
            {mathRows.map((row) => (
              <div
                key={row.label}
                className="flex items-center justify-between gap-2.5 border-b border-edge py-[7px] text-[14.5px] last:border-b-0"
              >
                <span className="text-ink-soft">{row.label}</span>
                <b
                  className={`whitespace-nowrap font-extrabold ${row.highlight ? 'text-status-active-text' : 'text-ink'}`}
                >
                  {row.value}
                </b>
              </div>
            ))}
            <p className="mt-3 text-[13.5px] text-ink-soft">{mathMicro}</p>
          </div>
          <p className="mt-3.5 text-[13.5px] text-ink-soft">{upsell}</p>
        </div>
      </div>
    </LandingSection>
  );
}
